import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { I18n } from '@/lib/api/types';
import { FieldShell, inputBase } from '@/lib/ui/Field';
import { cn } from '@/lib/ui/cn';

/**
 * Paired EN/AR input (spec §6.1). Per-field RTL on the AR side regardless of chrome
 * language (spec §6.3). Completeness chip. In translate-only mode the EN side is read-only.
 */
export function I18nField({
  label,
  value,
  onChange,
  required,
  error,
  multiline,
  translateOnly,
  disabled,
}: {
  label: string;
  value: I18n | undefined;
  onChange: (v: I18n) => void;
  required?: boolean;
  error?: string | null;
  multiline?: boolean;
  translateOnly?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const resolvedLabel = t(label, { defaultValue: label });
  const v: I18n = { en: value?.en ?? '', ar: value?.ar ?? '' };
  const [tab, setTab] = useState<'en' | 'ar'>(translateOnly ? 'ar' : 'en');
  const enComplete = !!v.en.trim();
  const arComplete = !!v.ar.trim();

  const chip = (
    <span className="flex items-center gap-[6px] font-mono text-label-sm uppercase tracking-[0.1em]">
      <span className={enComplete ? 'text-signal-teal' : 'text-ink-mute'}>EN{enComplete ? ' ✓' : ' ·'}</span>
      <span className={arComplete ? 'text-signal-teal' : 'text-signal-amber-ink'}>
        AR{arComplete ? ' ✓' : ' ·'}
      </span>
    </span>
  );

  const Tag = multiline ? 'textarea' : 'input';

  return (
    <FieldShell label={resolvedLabel} required={required} error={error} labelAdornment={chip}>
      <div className="flex overflow-hidden rounded-sm border-[0.5px] border-hairline">
        <button
          type="button"
          onClick={() => setTab('en')}
          className={cn(
            'px-sm py-[6px] font-mono text-label uppercase tracking-[0.1em]',
            tab === 'en' ? 'bg-accent-blue text-canvas-paper' : 'bg-canvas-deep-sand text-ink-mute',
          )}
        >
          EN
        </button>
        <button
          type="button"
          onClick={() => setTab('ar')}
          className={cn(
            'px-sm py-[6px] font-mono text-label uppercase tracking-[0.1em]',
            tab === 'ar' ? 'bg-accent-blue text-canvas-paper' : 'bg-canvas-deep-sand text-ink-mute',
          )}
        >
          AR
        </button>
      </div>
      {tab === 'en' ? (
        <Tag
          dir="ltr"
          lang="en"
          value={v.en}
          readOnly={translateOnly || disabled}
          onChange={(e: { target: { value: string } }) => onChange({ ...v, en: e.target.value })}
          className={cn(inputBase, multiline && 'min-h-[80px] resize-y', translateOnly && 'opacity-70')}
          aria-label={t('forms.fieldEnglish', { label: resolvedLabel })}
        />
      ) : (
        <Tag
          dir="rtl"
          lang="ar"
          value={v.ar}
          readOnly={disabled}
          onChange={(e: { target: { value: string } }) => onChange({ ...v, ar: e.target.value })}
          className={cn(inputBase, 'font-arabic', multiline && 'min-h-[80px] resize-y')}
          aria-label={t('forms.fieldArabic', { label: resolvedLabel })}
        />
      )}
    </FieldShell>
  );
}
